import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { api, get, post } from "lib/api";
import { useAuth } from "context/AuthContext";
import { isCustomerRole, isDistributorRole, isPortalRole } from "lib/portalRoles";
import { useMobileLocale } from "mobile/i18n";
import {
  AccountListGroup,
  AccountListRow,
  PortalAccountShell,
  accountFieldSx,
  portalChrome,
} from "mobile/portal/PortalAccountChrome";

const CHAT = {
  canvas: portalChrome.canvas,
  mine: portalChrome.iconInk,
  theirs: portalChrome.surface,
  accent: portalChrome.accent,
  accentHover: portalChrome.accentHover,
  brandRed: portalChrome.brandRed,
  compose: "rgba(247,248,250,0.96)",
  closed: "#147A5C",
};

function AccountBackButton({ onClick, label }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
        color: portalChrome.ink,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        transition: "transform 0.15s ease, background-color 0.15s ease",
        "&:active": { transform: "scale(0.94)" },
      }}
    >
      <Icon sx={{ fontSize: 18 }}>arrow_back_ios_new</Icon>
    </ButtonBase>
  );
}

AccountBackButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

function usePortalSession() {
  const auth = useAuth();
  const role = String(auth.pluginRole || "").toLowerCase();
  return {
    ...auth,
    role,
    isPortal: isPortalRole(role),
    isCustomer: isCustomerRole(role),
    isDistributor: isDistributorRole(role),
  };
}

function Guard({ children }) {
  const location = useLocation();
  const session = usePortalSession();
  if (!session.token) return <Navigate to="/m/authentication/sign-in" state={{ from: location }} replace />;
  if (session.authLoading || session.pluginRole == null) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", bgcolor: portalChrome.canvas }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (!session.isPortal) return <Navigate to="/m/post-login" replace />;
  return children;
}

Guard.propTypes = {
  children: PropTypes.node.isRequired,
};

async function uploadFiles(files) {
  const rows = Array.from(files || []).filter(Boolean);
  if (!rows.length) return [];
  const formData = new FormData();
  rows.forEach((file) => formData.append("files", file));
  const res = await api.post("/api/upload", formData);
  return (Array.isArray(res?.data) ? res.data : [])
    .map((row) => Number(row?.id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function absoluteMediaUrl(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const base = String(api?.defaults?.baseURL || "").replace(/\/+$/, "");
  return base ? `${base}${value.startsWith("/") ? value : `/${value}`}` : value;
}

function formatWhen(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const now = new Date();
  const sameDay =
    parsed.getFullYear() === now.getFullYear() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getDate() === now.getDate();
  if (sameDay) {
    return parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatMessageTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function isClosedStatus(status) {
  const key = String(status || "").toLowerCase();
  return key === "resolved" || key === "question_answered";
}

function PendingImageStrip({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <Stack direction="row" spacing={0.75} sx={{ px: 0.25, pb: 0.75, overflowX: "auto" }}>
      {files.map((file, index) => {
        const preview = URL.createObjectURL(file);
        return (
          <Box key={`${file.name}-${index}`} sx={{ position: "relative", flex: "0 0 auto" }}>
            <Box
              component="img"
              src={preview}
              alt=""
              onLoad={() => URL.revokeObjectURL(preview)}
              sx={{
                width: 64,
                height: 64,
                objectFit: "cover",
                borderRadius: "14px",
                border: `1px solid ${portalChrome.line}`,
                display: "block",
              }}
            />
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 22,
                height: 22,
                bgcolor: portalChrome.ink,
                color: "#fff",
                "&:hover": { bgcolor: portalChrome.ink },
              }}
            >
              <Icon sx={{ fontSize: 14 }}>close</Icon>
            </IconButton>
          </Box>
        );
      })}
    </Stack>
  );
}

PendingImageStrip.propTypes = {
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ImageLightbox({ urls, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex || 0);
  const safeUrls = (urls || []).map(absoluteMediaUrl).filter(Boolean);
  if (!safeUrls.length) return null;
  const current = safeUrls[Math.min(Math.max(index, 0), safeUrls.length - 1)];

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      PaperProps={{
        sx: { bgcolor: "rgba(10,10,12,0.96)", color: "#fff" },
      }}
    >
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          pt: "env(safe-area-inset-top)",
          pb: "env(safe-area-inset-bottom)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1 }}>
          <IconButton onClick={onClose} sx={{ color: "#fff" }} aria-label="Close">
            <Icon>close</Icon>
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            {index + 1} / {safeUrls.length}
          </Typography>
          <Box sx={{ width: 40 }} />
        </Box>
        <Box sx={{ flex: 1, display: "grid", placeItems: "center", px: 1.5 }}>
          <Box
            component="img"
            src={current}
            alt=""
            sx={{ maxWidth: "100%", maxHeight: "78dvh", objectFit: "contain", borderRadius: "8px" }}
          />
        </Box>
        {safeUrls.length > 1 ? (
          <Stack direction="row" spacing={1.25} justifyContent="center" sx={{ pb: 2.5 }}>
            <ButtonBase
              onClick={() => setIndex((prev) => (prev - 1 + safeUrls.length) % safeUrls.length)}
              sx={{
                px: 2,
                py: 1,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.12)",
                fontWeight: 700,
              }}
            >
              <Icon>chevron_left</Icon>
            </ButtonBase>
            <ButtonBase
              onClick={() => setIndex((prev) => (prev + 1) % safeUrls.length)}
              sx={{
                px: 2,
                py: 1,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.12)",
                fontWeight: 700,
              }}
            >
              <Icon>chevron_right</Icon>
            </ButtonBase>
          </Stack>
        ) : null}
      </Box>
    </Dialog>
  );
}

ImageLightbox.propTypes = {
  urls: PropTypes.arrayOf(PropTypes.string).isRequired,
  startIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

ImageLightbox.defaultProps = {
  startIndex: 0,
};

function MessageBubble({ message, t, onOpenImages }) {
  const mine = message?.senderRole !== "staff";
  const images = Array.isArray(message?.imageUrls) ? message.imageUrls.map(absoluteMediaUrl).filter(Boolean) : [];
  const body = String(message?.message || "").trim();
  const looksLikeImagePlaceholder = /^uploaded\s+\d+\s+images?\.?$/i.test(body);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: mine ? "flex-end" : "flex-start",
        px: 1.5,
        mb: 0.65,
      }}
    >
      <Box
        sx={{
          maxWidth: "78%",
          bgcolor: mine ? CHAT.mine : CHAT.theirs,
          color: mine ? "#fff" : portalChrome.ink,
          borderRadius: mine ? "20px 20px 6px 20px" : "20px 20px 20px 6px",
          px: 1.35,
          py: 1,
          boxShadow: portalChrome.lift,
          border: mine ? "none" : `1px solid ${portalChrome.line}`,
        }}
      >
        {!looksLikeImagePlaceholder && body ? (
          <Typography sx={{ fontSize: 15.5, lineHeight: 1.35, whiteSpace: "pre-wrap", fontWeight: 500 }}>
            {body}
          </Typography>
        ) : null}
        {images.length ? (
          <Stack
            direction="row"
            spacing={0.6}
            sx={{
              mt: body && !looksLikeImagePlaceholder ? 0.75 : 0,
              flexWrap: "wrap",
              rowGap: 0.6,
            }}
          >
            {images.map((url, imageIndex) => (
              <ButtonBase
                key={`${message.documentId}-${imageIndex}`}
                onClick={() => onOpenImages(images, imageIndex)}
                sx={{ borderRadius: "14px", overflow: "hidden" }}
              >
                <Box
                  component="img"
                  src={url}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: images.length === 1 ? 196 : 96,
                    height: images.length === 1 ? 196 : 96,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </ButtonBase>
            ))}
          </Stack>
        ) : null}
      </Box>
      <Typography
        sx={{
          mt: 0.35,
          px: 0.35,
          fontSize: 11,
          fontWeight: 650,
          color: portalChrome.subtle,
        }}
      >
        {mine ? t("You") : t("VBS Support")} · {formatMessageTime(message?.createdAt)}
      </Typography>
    </Box>
  );
}

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func.isRequired,
};

function SupportAvatar({ size = 40, warranty = false }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        background: warranty ? "#147A5C" : portalChrome.swoosh,
        display: "grid",
        placeItems: "center",
        color: "#fff",
        boxShadow: portalChrome.lift,
      }}
    >
      <Icon sx={{ fontSize: size * 0.48 }}>{warranty ? "verified_user" : "support_agent"}</Icon>
    </Box>
  );
}

SupportAvatar.propTypes = {
  size: PropTypes.number,
  warranty: PropTypes.bool,
};

SupportAvatar.defaultProps = {
  size: 40,
  warranty: false,
};

function subjectFromDraft(messageText, imageCount, t) {
  const cleaned = String(messageText || "").trim().replace(/\s+/g, " ");
  if (cleaned) return cleaned.slice(0, 80);
  if (imageCount > 0) return imageCount === 1 ? t("Photo") : t("Photos");
  return t("New message");
}

function ChatComposeBar({
  message,
  onMessageChange,
  files,
  onFilesChange,
  onSend,
  saving,
  placeholder,
  t,
}) {
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const canSend = Boolean(message.trim() || files.length);

  return (
    <Box
      sx={{
        bgcolor: CHAT.compose,
        backdropFilter: "blur(18px)",
        borderTop: `1px solid ${portalChrome.line}`,
        px: 1.1,
        pt: 0.7,
        pb: "calc(10px + env(safe-area-inset-bottom))",
      }}
    >
      <PendingImageStrip
        files={files}
        onRemove={(index) => onFilesChange(files.filter((_, i) => i !== index))}
      />
      {attachOpen ? (
        <Stack direction="row" spacing={1.25} sx={{ px: 0.5, pb: 0.85 }}>
          <ButtonBase
            onClick={() => cameraRef.current?.click()}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.45,
              minWidth: 64,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "16px",
                bgcolor: portalChrome.accent,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                boxShadow: "0 8px 16px rgba(255,92,0,0.28)",
              }}
            >
              <Icon sx={{ fontSize: 26 }}>photo_camera</Icon>
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 750, color: portalChrome.ink }}>{t("Camera")}</Typography>
          </ButtonBase>
          <ButtonBase
            onClick={() => libraryRef.current?.click()}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.45,
              minWidth: 64,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "16px",
                bgcolor: portalChrome.brandRed,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                boxShadow: "0 8px 16px rgba(224,24,32,0.24)",
              }}
            >
              <Icon sx={{ fontSize: 26 }}>photo_library</Icon>
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 750, color: portalChrome.ink }}>{t("Photos")}</Typography>
          </ButtonBase>
        </Stack>
      ) : null}
      <input
        ref={cameraRef}
        hidden
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(event) => {
          onFilesChange([...files, ...Array.from(event.target.files || [])]);
          event.target.value = "";
          setAttachOpen(false);
        }}
      />
      <input
        ref={libraryRef}
        hidden
        type="file"
        accept="image/*"
        multiple
        onChange={(event) => {
          onFilesChange([...files, ...Array.from(event.target.files || [])]);
          event.target.value = "";
          setAttachOpen(false);
        }}
      />
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.7 }}>
        <IconButton
          onClick={() => setAttachOpen((open) => !open)}
          sx={{
            width: 36,
            height: 36,
            mb: 0.2,
            color: "#fff",
            bgcolor: attachOpen ? portalChrome.subtle : CHAT.accent,
            "&:hover": { bgcolor: attachOpen ? portalChrome.muted : CHAT.accentHover },
          }}
          aria-label={t("Photos")}
        >
          <Icon sx={{ fontSize: 22 }}>{attachOpen ? "close" : "add"}</Icon>
        </IconButton>
        <Box
          sx={{
            flex: 1,
            bgcolor: portalChrome.surface,
            borderRadius: "22px",
            border: `1px solid ${portalChrome.line}`,
            px: 1.35,
            py: 0.45,
            minHeight: 40,
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={5}
            placeholder={placeholder || t("Message")}
            value={message}
            onChange={(event) => onMessageChange(event.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: 16,
                fontWeight: 500,
                py: 0.55,
                lineHeight: 1.3,
              },
            }}
          />
        </Box>
        <IconButton
          onClick={onSend}
          disabled={saving || !canSend}
          sx={{
            width: 36,
            height: 36,
            mb: 0.2,
            bgcolor: canSend ? CHAT.brandRed : "rgba(26,26,31,0.12)",
            color: "#fff",
            "&:hover": { bgcolor: canSend ? "#C4141C" : "rgba(26,26,31,0.12)" },
            "&.Mui-disabled": { bgcolor: "rgba(26,26,31,0.12)", color: "#fff" },
          }}
          aria-label={t("Send")}
        >
          <Icon sx={{ fontSize: 20 }}>{saving ? "hourglass_top" : "arrow_upward"}</Icon>
        </IconButton>
      </Box>
    </Box>
  );
}

ChatComposeBar.propTypes = {
  message: PropTypes.string.isRequired,
  onMessageChange: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  onFilesChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  placeholder: PropTypes.string,
  t: PropTypes.func.isRequired,
};

ChatComposeBar.defaultProps = {
  saving: false,
  placeholder: "",
};

export function MobilePortalSupportCompose() {
  const navigate = useNavigate();
  const { t } = useMobileLocale();
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    const messageText = message.trim();
    if (!messageText && !files.length) return;
    try {
      setSaving(true);
      setError("");
      const imageIds = await uploadFiles(files);
      const body =
        messageText ||
        `${t("Uploaded")} ${imageIds.length} ${imageIds.length === 1 ? t("image") : t("images")}.`;
      const res = await post("/api/support-chat/threads", {
        data: {
          subject: subjectFromDraft(messageText, imageIds.length, t),
          message: body,
          imageIds,
        },
      });
      const threadDocumentId = String(res?.data?.thread?.documentId || "").trim();
      if (threadDocumentId) {
        navigate(`/m/account/support/${threadDocumentId}`, { replace: true });
        return;
      }
      navigate("/m/account/support", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.message || t("Unable to start conversation."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Guard>
      <Box
        sx={{
          height: "100dvh",
          bgcolor: CHAT.canvas,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "rgba(247,248,250,0.94)",
            backdropFilter: "blur(18px)",
            borderBottom: `1px solid ${portalChrome.line}`,
            pt: "env(safe-area-inset-top)",
          }}
        >
          <Box sx={{ height: 3, background: portalChrome.swoosh }} />
          <Box sx={{ px: 2, py: 1, minHeight: 56, display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBackButton onClick={() => navigate("/m/account/support")} label={t("Back")} />
            <Typography
              sx={{
                flex: 1,
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: -0.35,
                color: portalChrome.ink,
              }}
            >
              {t("New Message")}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 0.95,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderTop: `1px solid ${portalChrome.line}`,
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 650, color: portalChrome.subtle, minWidth: 28 }}>
              {t("To:")}
            </Typography>
            <SupportAvatar size={28} />
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: "999px",
                bgcolor: portalChrome.accentSoft,
                color: portalChrome.accentHover,
                fontSize: 14,
                fontWeight: 750,
              }}
            >
              {t("VBS Support")}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            py: 4,
            textAlign: "center",
          }}
        >
          <SupportAvatar size={72} />
          <Typography sx={{ mt: 1.75, fontSize: 20, fontWeight: 820, letterSpacing: -0.35, color: portalChrome.ink }}>
            {t("VBS Support")}
          </Typography>
          <Typography sx={{ mt: 0.55, fontSize: 14.5, fontWeight: 600, color: portalChrome.muted, maxWidth: 280, lineHeight: 1.4 }}>
            {t("Send a text or photo — just like Messages.")}
          </Typography>
          {error ? (
            <Alert severity="error" sx={{ mt: 2, borderRadius: "14px", width: "100%", maxWidth: 360 }}>
              {error}
            </Alert>
          ) : null}
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <ChatComposeBar
            message={message}
            onMessageChange={setMessage}
            files={files}
            onFilesChange={setFiles}
            onSend={send}
            saving={saving}
            placeholder={t("Message")}
            t={t}
          />
        </Box>
      </Box>
    </Guard>
  );
}

function ReviewCard({ thread, onSubmitted }) {
  const { t } = useMobileLocale();
  const [stars, setStars] = useState(Number(thread?.review?.stars) || 0);
  const [comment, setComment] = useState(String(thread?.review?.comment || ""));
  const [saving, setSaving] = useState(false);
  const alreadyReviewed = !!thread?.review?.reviewedAt;

  if (!isClosedStatus(thread?.status)) return null;

  const submit = async () => {
    if (alreadyReviewed || stars < 1) return;
    try {
      setSaving(true);
      await post(`/api/support-chat/threads/${thread.documentId}/review`, {
        data: { stars, comment: comment.trim() || undefined },
      });
      onSubmitted();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        mx: 1.5,
        mb: 1.25,
        p: 1.35,
        borderRadius: "18px",
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: portalChrome.ink }}>
        {alreadyReviewed ? t("Thanks for your feedback") : t("Rate this conversation")}
      </Typography>
      <Stack direction="row" spacing={0.35} sx={{ mt: 0.75 }}>
        {[1, 2, 3, 4, 5].map((value) => (
          <IconButton
            key={value}
            disabled={alreadyReviewed}
            onClick={() => setStars(value)}
            sx={{ color: value <= stars ? "#F5A524" : portalChrome.subtle, p: 0.35 }}
          >
            <Icon>{value <= stars ? "star" : "star_border"}</Icon>
          </IconButton>
        ))}
      </Stack>
      {!alreadyReviewed ? (
        <>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder={t("Optional comment")}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            sx={{ ...accountFieldSx, mt: 1 }}
          />
          <ButtonBase
            onClick={submit}
            disabled={saving || stars < 1}
            sx={{
              mt: 1,
              width: "100%",
              py: 1.05,
              borderRadius: "12px",
              bgcolor: CHAT.closed,
              color: "#fff",
              fontWeight: 800,
              opacity: saving || stars < 1 ? 0.55 : 1,
            }}
          >
            {saving ? t("Sending...") : t("Submit review")}
          </ButtonBase>
        </>
      ) : null}
    </Box>
  );
}

ReviewCard.propTypes = {
  thread: PropTypes.object.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};

export function MobilePortalSupportThread() {
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const threadDocumentId = String(documentId || "").trim();

  const chatQuery = useQuery({
    queryKey: ["m-portal-support-thread", threadDocumentId],
    queryFn: () => get(`/api/support-chat/threads/${threadDocumentId}`),
    enabled: !!session.token && session.isPortal && !!threadDocumentId,
    staleTime: 4000,
    refetchInterval: 10000,
  });

  const thread = chatQuery.data?.data?.thread || null;
  const messages = useMemo(
    () => (Array.isArray(chatQuery.data?.data?.messages) ? chatQuery.data.data.messages : []),
    [chatQuery.data],
  );
  const closed = isClosedStatus(thread?.status);

  useEffect(() => {
    if (!threadDocumentId || !session.token || !session.isPortal) return undefined;
    post(`/api/support-chat/threads/${threadDocumentId}/read`, { data: {} }).catch(() => {});
    return undefined;
  }, [threadDocumentId, session.token, session.isPortal, messages.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, files.length]);

  const send = async () => {
    const trimmed = message.trim();
    if (!threadDocumentId || closed || (!trimmed && !files.length)) return;
    try {
      setSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(files);
      await post(`/api/support-chat/threads/${threadDocumentId}/messages`, {
        data: {
          message:
            trimmed ||
            `${t("Uploaded")} ${imageIds.length} ${imageIds.length === 1 ? t("image") : t("images")}.`,
          imageIds,
        },
      });
      setMessage("");
      setFiles([]);
      await chatQuery.refetch();
    } catch (error) {
      setNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.message || t("Unable to send message."),
      });
    } finally {
      setSaving(false);
    }
  };

  const title = thread?.subject || t("Support");
  const warrantyClaimId = String(thread?.topic?.documentId || "").trim();

  return (
    <Guard>
      <Box
        sx={{
          height: "100dvh",
          bgcolor: CHAT.canvas,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "rgba(247,248,250,0.94)",
            backdropFilter: "blur(18px)",
            borderBottom: `1px solid ${portalChrome.line}`,
            pt: "env(safe-area-inset-top)",
          }}
        >
          <Box sx={{ height: 3, background: portalChrome.swoosh }} />
          <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1, minHeight: 56 }}>
            <AccountBackButton onClick={() => navigate("/m/account/support")} label={t("Back")} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 800,
                  letterSpacing: -0.35,
                  color: portalChrome.ink,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("VBS Support")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: portalChrome.muted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
                {thread?.topic?.rmaNumber ? ` · ${thread.topic.rmaNumber}` : ""}
              </Typography>
            </Box>
            {warrantyClaimId ? (
              <ButtonBase
                onClick={() => navigate(`/m/account/warranty-claims/${warrantyClaimId}`)}
                sx={{
                  px: 1.1,
                  py: 0.65,
                  borderRadius: "999px",
                  bgcolor: portalChrome.accentSoft,
                  color: portalChrome.accentHover,
                  fontSize: 12,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {t("Claim")}
              </ButtonBase>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 1.25 }}>
          {chatQuery.isLoading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}
          {notice ? (
            <Alert severity={notice.type} sx={{ mx: 1.5, mb: 1, borderRadius: "14px" }}>
              {notice.text}
            </Alert>
          ) : null}
          {!messages.length && !chatQuery.isLoading ? (
            <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
              <Icon sx={{ fontSize: 36, color: portalChrome.subtle }}>chat_bubble_outline</Icon>
              <Typography sx={{ mt: 1, fontWeight: 750, color: portalChrome.muted }}>
                {t("Send a message to get help.")}
              </Typography>
            </Box>
          ) : null}
          {messages.map((row, index) => (
            <MessageBubble
              key={String(row.documentId || index)}
              message={row}
              t={t}
              onOpenImages={(urls, startIndex) => setLightbox({ urls, startIndex })}
            />
          ))}
          {thread ? <ReviewCard thread={thread} onSubmitted={() => chatQuery.refetch()} /> : null}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          {!closed ? (
            <ChatComposeBar
              message={message}
              onMessageChange={setMessage}
              files={files}
              onFilesChange={setFiles}
              onSend={send}
              saving={saving}
              placeholder={t("Message")}
              t={t}
            />
          ) : (
            <Box sx={{ px: 1.5, py: 1.25, pb: "calc(14px + env(safe-area-inset-bottom))", textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: portalChrome.subtle }}>
                {t("This conversation is closed.")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {lightbox ? (
        <ImageLightbox
          urls={lightbox.urls}
          startIndex={lightbox.startIndex}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </Guard>
  );
}

export function MobilePortalSupport() {
  const navigate = useNavigate();
  const session = usePortalSession();
  const { t } = useMobileLocale();

  const threadsQuery = useQuery({
    queryKey: ["m-portal-support-threads"],
    queryFn: () => get("/api/support-chat/threads"),
    enabled: !!session.token && session.isPortal,
    staleTime: 5000,
    refetchInterval: 12000,
  });

  const unreadQuery = useQuery({
    queryKey: ["m-portal-support-unread"],
    queryFn: () => get("/api/support-chat/unread-count"),
    enabled: !!session.token && session.isPortal,
    staleTime: 5000,
    refetchInterval: 12000,
  });

  const threads = useMemo(
    () => (Array.isArray(threadsQuery.data?.data) ? threadsQuery.data.data : []),
    [threadsQuery.data],
  );
  const unreadMessages = Number(unreadQuery.data?.data?.unreadMessages || 0);

  return (
    <Guard>
      <PortalAccountShell
        title={t("Support")}
        headerAction={
          <ButtonBase
            onClick={() => navigate("/m/account/support/new")}
            aria-label={t("New Message")}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color: portalChrome.brandRed,
              bgcolor: portalChrome.brandRedSoft,
              border: `1px solid ${portalChrome.line}`,
              "&:active": { transform: "scale(0.94)" },
            }}
          >
            <Icon sx={{ fontSize: 20 }}>edit</Icon>
          </ButtonBase>
        }
      >
        {unreadMessages > 0 ? (
          <Box
            sx={{
              mb: 1.25,
              px: 1.25,
              py: 0.9,
              borderRadius: "14px",
              bgcolor: portalChrome.accentSoft,
              color: portalChrome.accentHover,
              fontSize: 13,
              fontWeight: 750,
            }}
          >
            {unreadMessages === 1
              ? t("1 unread message")
              : t("{count} unread messages").replace("{count}", String(unreadMessages))}
          </Box>
        ) : null}

        {threadsQuery.isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {threadsQuery.isError ? (
          <Alert severity="error" sx={{ borderRadius: "14px", mb: 1.25 }}>
            {t("Unable to load conversations.")}
          </Alert>
        ) : null}

        {!threadsQuery.isLoading && !threads.length ? (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
              px: 2,
              borderRadius: "22px",
              bgcolor: portalChrome.surface,
              border: `1px solid ${portalChrome.line}`,
            }}
          >
            <SupportAvatar size={64} />
            <Typography sx={{ mt: 1.5, fontSize: 18, fontWeight: 820, letterSpacing: -0.3 }}>
              {t("Chat with support")}
            </Typography>
            <Typography sx={{ mt: 0.6, fontSize: 14, color: portalChrome.muted, fontWeight: 600, lineHeight: 1.45 }}>
              {t("Ask a question, share photos, and get help from the VBS team.")}
            </Typography>
            <ButtonBase
              onClick={() => navigate("/m/account/support/new")}
              sx={{
                mt: 2,
                px: 2.25,
                py: 1.15,
                borderRadius: "14px",
                bgcolor: portalChrome.brandRed,
                color: "#fff",
                fontWeight: 800,
              }}
            >
              {t("New Message")}
            </ButtonBase>
          </Box>
        ) : null}

        {threads.length ? (
          <AccountListGroup title={t("Conversations")}>
            {threads.map((thread, index) => {
              const preview =
                thread?.latestMessage?.message ||
                (thread?.latestMessage?.imageCount
                  ? `${thread.latestMessage.imageCount} ${
                      thread.latestMessage.imageCount === 1 ? t("photo") : t("photos")
                    }`
                  : t("No messages yet"));
              const unread = Number(thread?.unreadCount || 0) > 0;
              return (
                <AccountListRow
                  key={thread.documentId}
                  icon={thread?.threadType === "warranty_claim" ? "verified_user" : "forum"}
                  title={thread.subject || t("VBS Support")}
                  subtitle={preview}
                  meta={formatWhen(thread.lastMessageAt || thread.updatedAt)}
                  badge={unread ? unread : null}
                  onClick={() => navigate(`/m/account/support/${thread.documentId}`)}
                  divider={index < threads.length - 1}
                />
              );
            })}
          </AccountListGroup>
        ) : null}
      </PortalAccountShell>
    </Guard>
  );
}
